import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db, storage } from "../Config/firebase";
import { compressImage } from "../../utils/compressImage";
import RelatorioRupturaPaverPrint from "./RelatorioRupturaPaverPrint";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";
import {
  AREA_PADRAO_PAVER_MM2,
  DIAMETRO_PADRAO_PAVER_MM,
  calcularAreaPorDiametro,
  calcularCorposProva,
  calcularIdadeDias,
  calcularResumo,
  formatarDataBR,
  novoCorpoProva,
  numeroBR,
} from "./paverCalculos";
import "../CadastroExtracoes/DetalhesExtracao.css";
import { consultaTenantAtual } from "../Utils/tenant";

const COLLECTION = "tblRupturaPavers";

const inputTabelaSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
    p: "7px 4px",
  },
};

const cellCenterSx = {
  textAlign: "center",
  verticalAlign: "middle",
};

const inputCellSx = {
  ...cellCenterSx,
  px: "4px",
  py: "3px",
};

function formatarPrensaRuptura(prensa) {
  if (!prensa) return "";
  const partes = [
    prensa.codigo,
    prensa.empresa,
    prensa.equipamento,
    prensa.serie && `Série ${prensa.serie}`,
    prensa.dataCalibracao && `Calibração ${formatarDataBR(prensa.dataCalibracao)}`,
  ].filter(Boolean);
  return partes.join(" - ");
}

function extrairValorResistencia(valor) {
  return String(valor || "").replace(/[^\d,.]/g, "");
}

function formatarResistenciaEsperada(valor) {
  const numero = extrairValorResistencia(valor).trim();
  return numero ? `fpk ≥ ${numero} MPa` : "";
}

function prepararEnsaioParaSalvar(ensaio) {
  return {
    ...ensaio,
    resistenciaEsperada: ensaio?.resistenciaEsperadaNaoInformada
      ? ""
      : formatarResistenciaEsperada(ensaio?.resistenciaEsperada),
  };
}

export default function DetalhesRupturaPaver() {
  const { paverId } = useParams();
  const history = useHistory();
  const [ensaio, setEnsaio] = useState(null);
  const [corposProva, setCorposProva] = useState([]);
  const [calibracoesPrensa, setCalibracoesPrensa] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [openFotos, setOpenFotos] = useState(false);
  const [uploadingFotoIndex, setUploadingFotoIndex] = useState(null);
  const [fotosDraft, setFotosDraft] = useState([]);
  const ensaioOriginalRef = useRef(null);

  useEffect(() => {
    carregarEnsaio();
    carregarCalibracoesPrensa();
  }, [paverId]);

  async function carregarEnsaio() {
    const snap = await getDoc(doc(db, COLLECTION, paverId));
    if (!snap.exists()) {
      history.goBack();
      return;
    }

    const dados = { id: snap.id, ...snap.data() };
    dados.diametroCorpoProva = dados.diametroCorpoProva || DIAMETRO_PADRAO_PAVER_MM;
    dados.areaCorpoProva =
      dados.areaCorpoProva || calcularAreaPorDiametro(dados.diametroCorpoProva).toFixed(1);
    const lista = calcularCorposProva(
      dados.corposProva?.length ? dados.corposProva : Array.from({ length: 10 }, (_, index) => novoCorpoProva(index)),
      dados.areaCorpoProva || AREA_PADRAO_PAVER_MM2
    );
    setEnsaio(dados);
    setCorposProva(lista);
    ensaioOriginalRef.current = { ...dados, corposProva: lista };
  }

  async function carregarCalibracoesPrensa() {
    const q = query(collection(db, "tblCalibracaoPrensas"), orderBy("dataCalibracao", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setCalibracoesPrensa(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  function atualizarCampo(campo, valor) {
    if (campo === "diametroCorpoProva") {
      const areaCalculada = calcularAreaPorDiametro(valor || DIAMETRO_PADRAO_PAVER_MM);
      setEnsaio((prev) => ({
        ...prev,
        diametroCorpoProva: valor,
        areaCorpoProva: areaCalculada.toFixed(1),
      }));
      setCorposProva((prev) => calcularCorposProva(prev, areaCalculada));
      return;
    }

    setEnsaio((prev) => ({ ...prev, [campo]: valor }));

    if (campo === "areaCorpoProva") {
      setCorposProva((prev) => calcularCorposProva(prev, valor || AREA_PADRAO_PAVER_MM2));
    }
  }

  function atualizarCorpoProva(index, campo, valor) {
    setCorposProva((prev) => {
      const lista = [...prev];
      lista[index] = { ...lista[index], [campo]: valor };
      if (campo === "cargaRupturaTf" || campo === "fatorMultiplicador") {
        return calcularCorposProva(lista, ensaio?.areaCorpoProva || AREA_PADRAO_PAVER_MM2);
      }
      return lista;
    });
  }

  function gravarCampoSemCalculo(index, campo, valor) {
    setCorposProva((prev) => {
      const atual = prev[index]?.[campo] || "";
      if (String(atual) === String(valor || "")) return prev;

      const lista = [...prev];
      lista[index] = { ...lista[index], [campo]: valor };
      return lista;
    });
  }

  function handleEnterColuna(event, campo, index) {
    if (event.key !== "Enter") return;

    event.preventDefault();

    const proximo = document.querySelector(
      `[data-paver-campo='${campo}'][data-paver-index='${index + 1}']`
    );

    if (
      event.currentTarget &&
      campo !== "cargaRupturaTf" &&
      campo !== "fatorMultiplicador"
    ) {
      gravarCampoSemCalculo(index, campo, event.currentTarget.value);
    }

    if (proximo) proximo.focus();
  }

  function adicionarCorpoProva() {
    setCorposProva((prev) =>
      calcularCorposProva(
        [...prev, novoCorpoProva(prev.length)],
        ensaio?.areaCorpoProva || AREA_PADRAO_PAVER_MM2
      )
    );
  }

  function removerCorpoProva(index) {
    setCorposProva((prev) =>
      calcularCorposProva(
        prev.filter((_, itemIndex) => itemIndex !== index).map((item, itemIndex) => ({
          ...item,
          cp: String(itemIndex + 1),
        })),
        ensaio?.areaCorpoProva || AREA_PADRAO_PAVER_MM2
      )
    );
  }

  function fotosComMinimo() {
    const fotos = Array.isArray(ensaio?.relatorioFotografico)
      ? ensaio.relatorioFotografico
      : [];
    const quantidadeMinima = Math.max(0, corposProva.length);
    const total = Math.max(quantidadeMinima, fotos.length);

    return Array.from({ length: total }, (_, index) => ({
      id: fotos[index]?.id || `foto-${index + 1}`,
      legenda: fotos[index]?.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
      url: fotos[index]?.url || "",
      path: fotos[index]?.path || "",
      nomeArquivo: fotos[index]?.nomeArquivo || "",
    }));
  }

  function abrirCadastroFotos() {
    setFotosDraft(fotosComMinimo().map((foto) => ({ ...foto })));
    setOpenFotos(true);
  }

  function adicionarFotoExtra() {
    setFotosDraft((prev) => [
      ...prev,
      {
        id: `foto-${Date.now()}`,
        legenda: `FOTO ${String(prev.length + 1).padStart(2, "0")}`,
        url: "",
        path: "",
        nomeArquivo: "",
      },
    ]);
  }

  function selecionarImagemFoto(index, arquivo) {
    if (!arquivo) return;

    const previewUrl = URL.createObjectURL(arquivo);
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) =>
        itemIndex === index
          ? {
              ...foto,
              arquivo,
              previewUrl,
              nomeArquivo: arquivo.name,
            }
          : foto
      )
    );
  }

  function atualizarLegendaFoto(index, legenda) {
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) => (itemIndex === index ? { ...foto, legenda } : foto))
    );
  }

  function removerImagemFoto(index) {
    setFotosDraft((prev) =>
      prev.map((foto, itemIndex) =>
        itemIndex === index
          ? {
              ...foto,
              url: "",
              path: "",
              arquivo: null,
              previewUrl: "",
              nomeArquivo: "",
              removerPath: foto.path || foto.removerPath || "",
            }
          : foto
      )
    );
  }

  function cancelarFotos() {
    setFotosDraft([]);
    setOpenFotos(false);
  }

  async function salvarFotos() {
    if (!ensaio?.id) return;

    setUploadingFotoIndex(-1);
    try {
      const fotosSalvas = [];

      for (let index = 0; index < fotosDraft.length; index++) {
        const foto = fotosDraft[index];
        let fotoFinal = {
          id: foto.id || `foto-${index + 1}`,
          legenda: foto.legenda || `FOTO ${String(index + 1).padStart(2, "0")}`,
          url: foto.url || "",
          path: foto.path || "",
          nomeArquivo: foto.nomeArquivo || "",
        };

        if (foto.arquivo) {
          const arquivoComprimido = await compressImage(foto.arquivo, {
            maxWidth: 1400,
            maxHeight: 1400,
            quality: 0.72,
          });
          const caminho = `pavers/${ensaio.id}/relatorio-fotografico/foto-${index + 1}-${Date.now()}.jpg`;
          const storageRef = ref(storage, caminho);
          await uploadBytes(storageRef, arquivoComprimido, { contentType: "image/jpeg" });
          const url = await getDownloadURL(storageRef);

          if (foto.path) {
            deleteObject(ref(storage, foto.path)).catch(() => {});
          }

          fotoFinal = {
            ...fotoFinal,
            url,
            path: caminho,
            nomeArquivo: foto.arquivo.name,
          };
        } else if (foto.removerPath) {
          deleteObject(ref(storage, foto.removerPath)).catch(() => {});
        }

        fotosSalvas.push(fotoFinal);
      }

      await updateDoc(doc(db, COLLECTION, ensaio.id), {
        relatorioFotografico: fotosSalvas,
        atualizadoEm: new Date(),
      });

      setEnsaio((prev) => ({ ...prev, relatorioFotografico: fotosSalvas }));
      ensaioOriginalRef.current = {
        ...ensaioOriginalRef.current,
        relatorioFotografico: fotosSalvas,
      };
      setFotosDraft([]);
      setOpenFotos(false);
      setMensagem("Relatório fotográfico salvo com sucesso.");
    } finally {
      setUploadingFotoIndex(null);
    }
  }

  async function salvarTudo() {
    setSalvando(true);
    const areaCalculada = calcularAreaPorDiametro(
      ensaio?.diametroCorpoProva || DIAMETRO_PADRAO_PAVER_MM
    );
    const listaCalculada = calcularCorposProva(
      corposProva,
      areaCalculada
    );

    const salvo = await atualizarDocumentoComMesclagem({
      db,
      referencia: doc(db, COLLECTION, paverId),
      original: ensaioOriginalRef.current,
      atual: { ...ensaio, corposProva },
      preparar: (estado) => {
        const area = calcularAreaPorDiametro(
          estado.diametroCorpoProva || DIAMETRO_PADRAO_PAVER_MM
        );
        const corposCalculados = calcularCorposProva(estado.corposProva || [], area);
        const preparado = {
          ...prepararEnsaioParaSalvar(estado),
          areaCorpoProva: area.toFixed(1),
          quantidadeAmostras: corposCalculados.length,
          corposProva: corposCalculados,
        };
        delete preparado.id;
        return preparado;
      },
      metadados: { atualizadoEm: new Date() },
    });

    const salvoTela = { ...salvo, id: paverId };
    setCorposProva(salvoTela.corposProva || listaCalculada);
    setEnsaio(salvoTela);
    ensaioOriginalRef.current = salvoTela;
    setMensagem("Ensaio salvo com sucesso.");
    setSalvando(false);
  }

  const resumo = useMemo(
    () => calcularResumo(corposProva),
    [corposProva]
  );

  const idade = calcularIdadeDias(ensaio?.dataFabricacao, ensaio?.dataEnsaio);

  if (!ensaio) return null;

  if (modoPrint) {
    return (
      <RelatorioRupturaPaverPrint
        ensaio={{ ...ensaio, corposProva }}
        fotosRelatorio={(ensaio.relatorioFotografico || []).filter((foto) => foto.url)}
        onVoltar={() => setModoPrint(false)}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box className="extracao-actions">
        <Box className="extracao-actions-titulos">
          <Typography component="h1">Ruptura de Paver</Typography>
          <Typography component="p">
            {ensaio.codigo || "CE-RE-PE"}&nbsp;&nbsp; Rev.00 - {formatarDataBR(ensaio.dataRelatorio || ensaio.dataEnsaio)}
          </Typography>
        </Box>

        <Box className="extracao-actions-botoes">
          <Button variant="contained" onClick={salvarTudo} disabled={salvando} startIcon={<SaveIcon />}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
          <Tooltip title="Visualizar relatório">
            <IconButton color="primary" onClick={() => setModoPrint(true)} aria-label="Visualizar relatório">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Relatório fotográfico">
            <IconButton color="primary" onClick={abrirCadastroFotos} aria-label="Relatório fotográfico">
              <PhotoLibraryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Imprimir relatório">
            <IconButton color="primary" onClick={() => setModoPrint(true)} aria-label="Imprimir relatório">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" onClick={() => history.goBack()} disabled={salvando} startIcon={<ArrowBackIcon />}>
            Voltar
          </Button>
        </Box>
      </Box>

      <Box className="extracao-page" component="form" autoComplete="off">
        {mensagem && <Typography className="extracao-mensagem">{mensagem}</Typography>}

        <Dialog
          open={openFotos}
          onClose={(event, reason) => {
            if (uploadingFotoIndex !== null || reason === "backdropClick") return;
            setOpenFotos(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Relatório fotográfico</DialogTitle>
          <DialogContent dividers>
            <Typography className="fotos-dialog-subtitulo">
              {corposProva.length} corpo(s) de prova cadastrado(s). Cadastre uma foto por CP e adicione extras quando necessário.
            </Typography>

            <Box className="fotos-extracao-grid">
              {fotosDraft.map((foto, index) => (
                <Paper className="foto-extracao-card" key={foto.id || index} variant="outlined">
                  <Box className="foto-extracao-preview">
                    {foto.previewUrl || foto.url ? (
                      <img src={foto.previewUrl || foto.url} alt={foto.legenda || `Foto ${index + 1}`} />
                    ) : (
                      <AddPhotoAlternateIcon />
                    )}
                  </Box>

                  <TextField
                    size="small"
                    label="Legenda"
                    value={foto.legenda}
                    onChange={(e) => atualizarLegendaFoto(index, e.target.value)}
                    fullWidth
                  />

                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button component="label" size="small" variant="contained" disabled={uploadingFotoIndex !== null}>
                      {foto.arquivo ? "Selecionada" : "Imagem"}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={(e) => selecionarImagemFoto(index, e.target.files?.[0])}
                      />
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removerImagemFoto(index)}
                      disabled={(!foto.url && !foto.previewUrl) || uploadingFotoIndex !== null}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={adicionarFotoExtra} startIcon={<AddPhotoAlternateIcon />} disabled={uploadingFotoIndex !== null}>
              Acrescentar imagem
            </Button>
            <Button onClick={cancelarFotos} disabled={uploadingFotoIndex !== null}>
              Cancelar
            </Button>
            <Button onClick={salvarFotos} variant="contained" disabled={uploadingFotoIndex !== null}>
              {uploadingFotoIndex !== null ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>
            DADOS DO ENSAIO
          </Typography>

          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={2}>
            <TextField label="Data relatório" type="date" InputLabelProps={{ shrink: true }} value={ensaio.dataRelatorio || ""} size="small" onChange={(e) => atualizarCampo("dataRelatorio", e.target.value)} />
            <TextField label="Data ensaio" type="date" InputLabelProps={{ shrink: true }} value={ensaio.dataEnsaio || ""} size="small" onChange={(e) => atualizarCampo("dataEnsaio", e.target.value)} />
            <Stack spacing={0.5}>
              <TextField
                label="Data fabricação"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={ensaio.dataFabricacao || ""}
                size="small"
                onChange={(e) => atualizarCampo("dataFabricacao", e.target.value)}
                disabled={ensaio.dataFabricacaoNaoInformada === true}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={ensaio.dataFabricacaoNaoInformada === true}
                    onChange={(e) =>
                      setEnsaio((atual) => ({
                        ...atual,
                        dataFabricacaoNaoInformada: e.target.checked,
                        dataFabricacao: e.target.checked
                          ? ""
                          : atual.dataFabricacao,
                        lote: e.target.checked ? "" : atual.lote,
                      }))
                    }
                  />
                }
                label="Não informada"
              />
            </Stack>
            <TextField label="Idade (dias)" value={idade || ""} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Cliente" value={ensaio.cliente || "-"} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Obra" value={ensaio.obra || "-"} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Código" value={ensaio.codigo || "-"} size="small" onChange={(e) => atualizarCampo("codigo", e.target.value)} />
            <TextField label="Característica do piso" value={ensaio.caracteristicaPiso || ""} size="small" onChange={(e) => atualizarCampo("caracteristicaPiso", e.target.value)} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
            <TextField label="Local de aplicação" value={ensaio.localAplicacao || ""} size="small" onChange={(e) => atualizarCampo("localAplicacao", e.target.value)} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
            <Stack spacing={0.5}>
              <TextField
                label="Resistência esperada"
                value={formatarResistenciaEsperada(ensaio.resistenciaEsperada)}
                size="small"
                onChange={(e) => atualizarCampo("resistenciaEsperada", formatarResistenciaEsperada(e.target.value))}
                disabled={ensaio.resistenciaEsperadaNaoInformada === true}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={ensaio.resistenciaEsperadaNaoInformada === true}
                    onChange={(e) =>
                      setEnsaio((atual) => ({
                        ...atual,
                        resistenciaEsperadaNaoInformada: e.target.checked,
                        resistenciaEsperada: e.target.checked
                          ? ""
                          : atual.resistenciaEsperada,
                      }))
                    }
                  />
                }
                label="Não informada"
              />
            </Stack>
            <TextField label="Formato tipo" value={ensaio.formatoTipo || ""} size="small" onChange={(e) => atualizarCampo("formatoTipo", e.target.value)} />
            <TextField label="Diâmetro do corpo de prova (mm)" value={ensaio.diametroCorpoProva || ""} size="small" onChange={(e) => atualizarCampo("diametroCorpoProva", e.target.value)} />
            <TextField label="Área calculada (mm²)" value={numeroBR(ensaio.areaCorpoProva || AREA_PADRAO_PAVER_MM2, 1)} size="small" InputProps={{ readOnly: true }} />
            <TextField select label="Prensa utilizada" value={ensaio.prensaRuptura?.id || ""} size="small" onChange={(e) => {
              const prensa = calibracoesPrensa.find((item) => item.id === e.target.value);
              atualizarCampo("prensaRuptura", prensa ? { id: prensa.id, ...prensa } : null);
            }}>
              <MenuItem value="">Selecione a prensa/certificado</MenuItem>
              {calibracoesPrensa.map((prensa) => (
                <MenuItem key={prensa.id} value={prensa.id}>
                  {formatarPrensaRuptura(prensa)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} mb={2}>
            <Typography fontWeight={700}>RESULTADOS DO ENSAIO</Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={adicionarCorpoProva}>
              Adicionar CP
            </Button>
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={cellCenterSx}>CP Nº</TableCell>
                  <TableCell sx={cellCenterSx}>Largura (L)</TableCell>
                  <TableCell sx={cellCenterSx}>Comprim. (C)</TableCell>
                  <TableCell sx={cellCenterSx}>Altura (H)</TableCell>
                  <TableCell sx={cellCenterSx}>Massa (g)</TableCell>
                  <TableCell sx={cellCenterSx}>Carga ruptura (Tf)</TableCell>
                  <TableCell sx={cellCenterSx}>Força ruptura (kN)</TableCell>
                  <TableCell sx={cellCenterSx}>Fator (p)</TableCell>
                  <TableCell sx={cellCenterSx}>Resistência (MPa)</TableCell>
                  <TableCell sx={cellCenterSx}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corposProva.map((item, index) => (
                  <TableRow key={index}>
                    {[
                      ["cp", "CP"],
                      ["largura", "L"],
                      ["comprimento", "C"],
                      ["altura", "H"],
                      ["massa", "Massa"],
                      ["cargaRupturaTf", "Tf"],
                    ].map(([campo, label]) => {
                      const calculaAoDigitar = campo === "cargaRupturaTf";

                      return (
                        <TableCell key={campo} sx={{ ...inputCellSx, minWidth: campo === "cp" ? 70 : 110 }}>
                          <TextField
                            key={`${index}-${campo}`}
                            size="small"
                            placeholder={label}
                            defaultValue={item[campo] || ""}
                            onChange={
                              calculaAoDigitar
                                ? (e) => atualizarCorpoProva(index, campo, e.target.value)
                                : undefined
                            }
                            onBlur={
                              calculaAoDigitar
                                ? undefined
                                : (e) => gravarCampoSemCalculo(index, campo, e.target.value)
                            }
                            onKeyDown={(e) => handleEnterColuna(e, campo, index)}
                            sx={inputTabelaSx}
                            inputProps={{
                              "data-paver-campo": campo,
                              "data-paver-index": index,
                            }}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell sx={cellCenterSx}>{numeroBR(item.forcaRupturaKn, 3)}</TableCell>
                    <TableCell sx={{ ...inputCellSx, minWidth: 100 }}>
                      <TextField
                        size="small"
                        placeholder="p"
                        value={item.fatorMultiplicador || ""}
                        onChange={(e) => atualizarCorpoProva(index, "fatorMultiplicador", e.target.value)}
                        onKeyDown={(e) => handleEnterColuna(e, "fatorMultiplicador", index)}
                        sx={inputTabelaSx}
                        inputProps={{
                          "data-paver-campo": "fatorMultiplicador",
                          "data-paver-index": index,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellCenterSx}>
                      <Typography fontWeight={700}>{numeroBR(item.resistenciaRupturaMpa, 1)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => removerCorpoProva(index)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} mb={2}>RESUMO</Typography>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={2}>
            <TextField label="Valor médio (MPa)" value={numeroBR(resumo.media, 1)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Desvio padrão (MPa)" value={numeroBR(resumo.desvioPadrao, 2)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Coeficiente Student" value={numeroBR(resumo.coeficienteStudent, 3)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="fpk estimado (MPa)" value={numeroBR(resumo.fpkEstimado, 1)} size="small" InputProps={{ readOnly: true }} />
            <TextField label="Comentários adversos" value={ensaio.comentarios || ""} onChange={(e) => atualizarCampo("comentarios", e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
            <TextField label="Observação" value={ensaio.observacao || ""} onChange={(e) => atualizarCampo("observacao", e.target.value)} multiline rows={3} sx={{ gridColumn: { xs: "auto", md: "span 2" } }} />
          </Box>
        </Paper>
      </Box>
    </>
  );
}
